import { useEffect } from "react";

import { useMutation } from "@tanstack/react-query";

import { onToast } from "@components/Toast";

import { blacklistAPI } from "@api/blacklist";
import { processRequest } from "@api/config";

import { useWebSocketStore } from "@storage/global/useWebSocketStore";

import { useAuthQuery } from "@lib/queryClient";

import { API_PREFIXES, QUERY_KEYS } from "@configs/api";
import { NOTIFICATION_DURATION } from "@configs/constants";

export const useBlacklistQuery = () => {
	const { updateBannedUserList } = useWebSocketStore();

	const query = useAuthQuery({
		queryKey: [QUERY_KEYS.BLACK_LIST_DATA],
		queryFn: async () => {
			const data = await processRequest({
				prefix: API_PREFIXES.GET_BLACK_LIST,
				request: (token) => blacklistAPI.getBlackListAsync({ token }),
			});

			return data;
		},
	});

	useEffect(() => {
		if (query.isSuccess) {
			updateBannedUserList(query.data);
		}
	}, [query.data, query.isSuccess]);

	return null;
};

export const useBlockUserByIdMutation = () =>
	useMutation({
		mutationFn: async ({ userId, reason, term }: { userId: string; reason: string; term: string }) => {
			const { code, message } = await processRequest({
				prefix: API_PREFIXES.BLOCK_USER_BY_ID,
				request: (token) => blacklistAPI.blockUserByIdAsync({ id: userId, reason, term, token }),
			});

			return { code, message };
		},
		onSuccess(data) {
			onToast({
				message: "Success",
				description: data.message,
				duration: NOTIFICATION_DURATION.SHORT,
			});
		},
	});

export const useUnblockUserByIdMutation = () =>
	useMutation({
		mutationFn: async ({ userId }: { userId: string }) => {
			await processRequest({
				prefix: API_PREFIXES.UNBLOCK_USER_BY_ID,
				request: (token) => blacklistAPI.unBlockUserByIdAsync({ userId, token }),
			});
		},
	});
